import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Gift,
  Sparkles,
  Tag,
  Clock,
  ArrowRight,
  PartyPopper,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypeStyles = {
  promotion: { bg: "bg-[#C5A059]/10", text: "text-[#C5A059]", label: "Promotion" },
  holiday: { bg: "bg-red-500/10", text: "text-red-400", label: "Holiday Sale" },
  seasonal: { bg: "bg-emerald-500/10", text: "text-emerald-400", label: "Seasonal" },
  flash_sale: { bg: "bg-purple-500/10", text: "text-purple-400", label: "Flash Sale" },
  new_course: { bg: "bg-blue-500/10", text: "text-blue-400", label: "New Course" },
  special: { bg: "bg-[#C5A059]/10", text: "text-[#C5A059]", label: "Special Event" },
};

export const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="events-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              What's Happening
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Events &<br />
              <span className="gold-gradient">Promotions</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] max-w-2xl mx-auto">
              Stay updated with our latest promotions, holiday specials, and exclusive events.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/gift-cards"
              data-testid="events-gift-card-link"
              className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-6 py-3 text-sm font-body tracking-wider uppercase hover:bg-[#C5A059]/10 transition-all"
            >
              <Gift size={16} /> Gift Cards
            </Link>
            <Link
              to="/special-courses"
              className="flex items-center gap-2 border border-[#262626] text-[#A3A3A3] px-6 py-3 text-sm font-body tracking-wider uppercase hover:border-[#C5A059] hover:text-white transition-all"
            >
              <Sparkles size={16} /> Special Courses
            </Link>
            <Link
              to="/book"
              className="flex items-center gap-2 border border-[#262626] text-[#A3A3A3] px-6 py-3 text-sm font-body tracking-wider uppercase hover:border-[#C5A059] hover:text-white transition-all"
            >
              <Calendar size={16} /> Book Now
            </Link>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {loading && (
            <div className="text-center py-20 text-[#A3A3A3]">Loading events...</div>
          )}

          {!loading && events.length === 0 && (
            <div className="text-center py-20">
              <PartyPopper className="w-16 h-16 text-[#262626] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">No Active Events</h3>
              <p className="font-body text-sm text-[#A3A3A3] mb-8">
                Check back soon for exciting promotions and holiday specials!
              </p>
              <div className="flex gap-4 justify-center">
                <Link
                  to="/gift-cards"
                  className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all"
                >
                  Browse Gift Cards
                </Link>
                <Link
                  to="/book"
                  className="border border-[#C5A059] text-[#C5A059] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#C5A059]/10 transition-all"
                >
                  Book a Service
                </Link>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {events.map((event) => {
              const style = eventTypeStyles[event.event_type] || eventTypeStyles.special;
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bento-card overflow-hidden"
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {event.image_url && (
                      <div className="md:w-72 h-48 md:h-auto flex-shrink-0">
                        <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-6 md:p-8 flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-3 py-1 ${style.bg} ${style.text}`}>
                          {style.label}
                        </span>
                        {event.discount && (
                          <span className="flex items-center gap-1 text-xs text-[#C5A059]">
                            <Tag size={12} /> {event.discount}
                          </span>
                        )}
                      </div>

                      <h3 className="font-heading text-xl md:text-2xl text-white mb-3">
                        {event.title}
                      </h3>

                      <p className="font-body text-sm text-[#A3A3A3] leading-relaxed mb-4">
                        {event.description}
                      </p>

                      {(event.date || event.end_date) && (
                        <div className="flex items-center gap-2 text-xs text-[#A3A3A3] mb-4">
                          <Clock size={12} className="text-[#C5A059]" />
                          <span>
                            {event.date}{event.end_date ? ` — ${event.end_date}` : ""}
                          </span>
                        </div>
                      )}

                      {event.promo_code && (
                        <div className="inline-block bg-[#C5A059]/10 border border-[#C5A059]/30 px-4 py-2 mb-4">
                          <span className="text-xs text-[#A3A3A3]">Promo Code: </span>
                          <span className="font-heading text-lg text-[#C5A059]">{event.promo_code}</span>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Link
                          to="/book"
                          className="bg-[#C5A059] text-[#050505] font-semibold text-xs tracking-[0.15em] uppercase px-6 py-3 hover:brightness-110 transition-all"
                        >
                          Book Now
                        </Link>
                        <Link
                          to="/contact"
                          className="border border-[#C5A059] text-[#C5A059] font-semibold text-xs tracking-[0.15em] uppercase px-6 py-3 hover:bg-[#C5A059]/10 transition-all"
                        >
                          Learn More
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gift Card CTA */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bento-card p-10 md:p-16 text-center">
            <Gift className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-4">
              Give the Gift of Beauty
            </h2>
            <p className="font-body text-[#A3A3A3] max-w-xl mx-auto mb-8">
              Our digital gift cards make the perfect present for any occasion.
              Available in any amount.
            </p>
            <Link
              to="/gift-cards"
              data-testid="events-gift-cta"
              className="inline-flex items-center gap-2 bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all"
            >
              Shop Gift Cards <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
